import React, { useEffect, useRef, useState } from 'react';
import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import { useFacility } from '../../context/FacilityContext';
import { Alert, Box } from '@mui/material';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import RoomManagerForm from './RoomManagerForm';
import RoomManagerTable from './RoomManagerTable';
import { getNewYorkTimeZoneDate } from '../../utils/dayjsUs';
import dayjs from 'dayjs';
import DevelopmentOnly from '../../Components/Dashboard/DevelopmentOnly/DevelopmentOnly';

function RoomManager() {
  const { facilityId } = useFacility();
  const formRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [groups, setGroups] = useState([]);
  const [currentDate, setCurrentDate] = useState(getNewYorkTimeZoneDate());

  const [infoAlert, setInfoAlert] = useState('');
  const [errorAlert, setErrorAlert] = useState('');
  const resetAlerts = () => {
    setInfoAlert('');
    setErrorAlert('');
  };

  const submitRecord = async (data) => {
    resetAlerts();

    data = { ...data, facilityId };

    setIsLoading(true);
    const q = query(
      collection(db, 'rooms'),
      where('facilityId', '==', facilityId),
      where('roomName', '==', data.roomName),
    );
    const existingDocs = await getDocs(q);

    if (!existingDocs.empty) {
      setErrorAlert('This room name is already assigned.');
      setIsLoading(false);
      return;
    }

    try {
      const docRef = await addDoc(collection(db, 'rooms'), data);
      const newData = { ...data, id: docRef.id };

      const rolesQuery = query(collection(db, 'roles'));
      const rolesSnapshot = await getDocs(rolesQuery);
      const roles = rolesSnapshot.docs.map((doc) => doc.data());

      for (const role of roles) {
        let usersQuery2 = query(
          collection(db, 'users'),
          where('facilities', 'array-contains', facilityId),
          where('subRole', '==', role.roleName),
        );

        const usersWithRoleInFacility = await getDocs(usersQuery2);
        const users = usersWithRoleInFacility.docs.map((doc) => {
          return { ...doc.data(), uid: doc.id };
        });
        const channelData = {
          ChannelColorCode: role.roleChatColor,
          LastMessageSent: '',
          RoomName: role.roleName,
          RoomOwner: docRef,
          RoomUsers: [docRef, ...users.map((u) => doc(db, 'users', u.uid))],
          StaffRoomName: newData.roomName,
          UpToDateUsers: users.map((u) => 'users/' + u.uid),
          creationTime: dayjs().toDate(),
          facilityId: facilityId,
        };

        await addDoc(collection(db, 'channels'), channelData);
      }
      setInfoAlert('Room added successfully.');
      setRecords((prevRecords) => [...prevRecords, newData]);
    } catch (error) {
      setErrorAlert(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  const genrateLogInCode = async (roomId) => {
    setIsLoading(true);
    const generatedCode = Math.floor(
      100000 + Math.random() * 900000,
    ).toString();
    const expirationDate = getNewYorkTimeZoneDate().add(5, 'minutes').toDate();

    try {
      const docRef = doc(db, 'rooms', roomId);
      await updateDoc(docRef, {
        logInCode: generatedCode,
        expirationTime: expirationDate,
      });

      setRecords((prevRecords) => {
        return prevRecords.map((r) => {
          if (r.id === roomId) {
            return {
              ...r,
              logInCode: generatedCode,
              expirationTime: expirationDate,
            };
          }
          return r;
        });
      });

      setInfoAlert(
        'The login code was generated successfully and is valid for 5 minutes.',
      );
    } catch (error) {
      console.error('Error generating log in code:', error);
      setErrorAlert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const logOutRoom = async (roomId) => {
    setIsLoading(true);

    try {
      const docRef = doc(db, 'rooms', roomId);
      await updateDoc(docRef, {
        logedIn: false,
      });
      setRecords((prevRecords) => {
        const updatedRecords = prevRecords.map((r) => {
          if (r.id === roomId) {
            return { ...r, logedIn: false };
          }
          return r;
        });
        return updatedRecords;
      });
    } catch (error) {
      console.error('Error deleting room:', error);
      setErrorAlert(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  const deleteRoom = async (room) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to remove the device with ID ${room.roomName}?`,
    );

    if (!confirmDelete) {
      return;
    }

    setIsLoading(true);

    try {
      const docRef = doc(db, 'rooms', room.id);
      await deleteDoc(docRef);
      setInfoAlert(`Successfully deleted the room.`);
      setRecords((prevRecords) => prevRecords.filter((d) => d.id !== room.id));
    } catch (error) {
      console.error('Error deleting room:', error);
      setErrorAlert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      let conditions = [where('facilityId', '==', facilityId)];
      const q = query(collection(db, 'rooms'), ...conditions);

      if (formRef.current && formRef.current.resetForm) {
        formRef.current.resetForm();
      }

      const querySnapshot = await getDocs(q);
      const recordsArray = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        const expirationTimeDate = data.expirationTime
          ? getNewYorkTimeZoneDate(data.expirationTime.toDate())
          : null;

        const logInCode =
          expirationTimeDate && currentDate.isBefore(expirationTimeDate)
            ? data.logInCode
            : null;

        return {
          ...data,
          id: doc.id,
          logInCode,
        };
      });
      setRecords(recordsArray);
    } catch (error) {
      setErrorAlert(error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getGroupsData = async () => {
    const q = query(
      collection(db, 'groups'),
      where('facilityId', '==', facilityId),
    );

    try {
      const querySnapshot = await getDocs(q);
      let groupsData = [];
      querySnapshot.forEach((doc) => {
        groupsData.push({ ...doc.data(), id: doc.id });
      });
      setGroups(groupsData);
    } catch (error) {
      console.error('Error fetching groups data:', error);
    }
  };
  useEffect(() => {
    if (!facilityId) return;
    setIsLoading(true);

    fetchRooms();
    getGroupsData();
  }, [facilityId]);
  useEffect(() => {
    setRecords((prevRecords) => {
      return prevRecords.map((r) => {
        if (!r.expirationTime) {
          return r;
        }

        // Check if expirationTime is a Firestore Timestamp or already a Date
        const expirationTimeDate =
          typeof r.expirationTime.toDate === 'function'
            ? r.expirationTime.toDate()
            : r.expirationTime;

        if (expirationTimeDate < currentDate.toDate()) {
          return { ...r, logInCode: null };
        }
        return r;
      });
    });
  }, [currentDate]);

  useEffect(() => {
    const interval = setInterval(
      () => {
        setCurrentDate(getNewYorkTimeZoneDate());
      },
      60 * 1000, // 1 minute
    );

    return () => clearInterval(interval);
  }, []);
  return (
    <DashboardLayout facilityId={facilityId} isLoading={isLoading}>
      <DevelopmentOnly fallback={<p>This module is under development.</p>}>
        <Box sx={{ marginTop: '15px' }}>
          <h2 style={{ marginBottom: '10px' }}>Add Room</h2>

          {infoAlert && (
            <Alert
              severity="success"
              onClose={() => {
                setInfoAlert('');
              }}
            >
              {infoAlert}
            </Alert>
          )}
          {errorAlert && (
            <Alert
              severity="error"
              onClose={() => {
                setErrorAlert('');
              }}
            >
              {errorAlert}
            </Alert>
          )}

          <Box sx={{ maxWidth: 768, marginBottom: '15px' }}>
            <RoomManagerForm
              ref={formRef}
              onSubmit={submitRecord}
              groups={groups}
            />
          </Box>

          <h2 style={{ marginBottom: '24px', marginTop: '48px' }}>
            Current Rooms
          </h2>

          <Box>
            <RoomManagerTable
              records={records}
              onDelete={deleteRoom}
              logOutRoom={logOutRoom}
              genrateLogInCode={genrateLogInCode}
            />
          </Box>
        </Box>
      </DevelopmentOnly>
    </DashboardLayout>
  );
}

RoomManager.propTypes = {};

export default RoomManager;
